import {
    DestinationEvent,
    DestinationResponse,
    FormattedEvent,
    FormattedEventResponse,
    transformDestinationEvent,
} from '@eventbrite/event-renderer';
import { sdkRequest, translateServerErrors } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import isEmpty from 'lodash/isEmpty';

export interface PaginationResponse {
    object_count: number;
    page_number: number;
    page_size: number;
    page_count: number;
    has_more_items: boolean;
}

export interface DateInfo {
    timezone: string;
    local: string;
    utc: string;
}

interface TicketPrice {
    currency: string;
    major_value: string;
    value: number;
    display: string;
}

interface OrganizerEventResponse {
    name: {
        text: string;
        html: string;
    };
    description?: {
        text: string;
        html: string;
    };
    url: string;
    vanity_url?: string;
    start: DateInfo;
    end: DateInfo;
    organization_id: string;
    created: string;
    changed: string;
    published: string;
    capacity: number;
    capacity_is_custom: boolean;
    status: string;
    currency: string;
    listed: boolean;
    shareable: boolean;
    invite_only: boolean;
    online_event: boolean;
    show_remaining: boolean;
    tx_time_limit: number;
    hide_start_date: boolean;
    hide_end_date: boolean;
    locale: string;
    is_locked: boolean;
    privacy_setting: string;
    is_series: boolean;
    is_series_parent: boolean;
    inventory_type: string;
    is_reserved_seating: boolean;
    show_pick_a_seat: boolean;
    show_seatmap_thumbnail: boolean;
    show_colors_in_seatmap_thumbnail: boolean;
    source: string;
    is_free: boolean;
    version: string | null;
    summary: string;
    logo_id: string;
    organizer_id: string;
    venue_id: string;
    category_id: string | null;
    subcategory_id: string | null;
    format_id: string | null;
    id: string;
    resource_uri: string;
    is_externally_ticketed: boolean;
    logo: {
        crop_mask: {
            top_left: {
                x: number;
                y: number;
            };
            width: number;
            height: number;
        };
        original: {
            url: string;
            width: number | null;
            height: number | null;
        };
        id: string;
        url: string;
        aspect_ratio: string;
        edge_color: string | null;
        edge_color_set: boolean;
    };
    ticket_availability: {
        has_available_tickets: boolean;
        minimum_ticket_price: TicketPrice;
        maximum_ticket_price: TicketPrice;
        is_sold_out: boolean;
        start_sales_date: {
            timezone: string;
            local: string;
            utc: string;
        };
        waitlist_available: boolean;
    };
}

interface OrganizerEventsResponse {
    events: Array<OrganizerEventResponse>;
    pagination: PaginationResponse;
}

const STANDARD_SERVER_ERROR = gettext('An error occured.');

const GENERAL_ERROR_SPEC = {
    default: () => STANDARD_SERVER_ERROR,
};

const _getRelatedEventsApiUrl = (eventId: string): string =>
    `/destination/events/${eventId}/related/?expand=image,primary_venue,ticket_availability,saves,primary_organizer`;

export const _getOrganizerEventsApiUrl = (organizerId: string): string =>
    `/organizers/${organizerId}/events/?expand=ticket_availability&status=live&only_public=true`;

export const _getOrganizerDestinationEventsApiUrl = (
    eventIds: string,
): string =>
    `/destination/events/?event_ids=${eventIds}&expand=event_sales_status,ticket_availability,image,primary_venue,saves,primary_organizer&include_parent_events=true`;

export const excludeEventOwnerId = (
    { events }: DestinationResponse,
    eventOwnerId: string,
) => {
    return events.filter(
        (event: DestinationEvent) => event.id !== eventOwnerId,
    );
};

export const excludeDuplicatedEvents = (
    events: FormattedEvent[],
    eventIdsToExclude: string[],
) => events.filter((event) => !eventIdsToExclude.includes(event.id));

export const getRelatedEvents = async (
    eventId: string,
): Promise<FormattedEventResponse> => {
    return sdkRequest(_getRelatedEventsApiUrl(eventId))
        .then((response: DestinationResponse) => {
            const events = excludeEventOwnerId(response, eventId);

            return {
                events: events.map((event: DestinationEvent) =>
                    transformDestinationEvent(event),
                ),
            };
        })
        .catch(translateServerErrors(GENERAL_ERROR_SPEC));
};

export const getOrganizerEvents = (
    organizerId: string,
): Promise<OrganizerEventsResponse> =>
    sdkRequest(_getOrganizerEventsApiUrl(organizerId))
        .then((response: OrganizerEventsResponse) => response)
        .catch(translateServerErrors(GENERAL_ERROR_SPEC));

export const getOpenSaleEvents = (
    events: OrganizerEventResponse[],
): OrganizerEventResponse[] =>
    events?.filter(
        (event: OrganizerEventResponse) =>
            event.ticket_availability?.has_available_tickets,
    );

export const filterEvents = (
    amountOfEvents: number,
    events: any[],
    eventOwnerId: string,
) => {
    const eventsWithoutOwner = events?.filter((event: any) => {
        return event.id !== eventOwnerId;
    });
    return eventsWithoutOwner?.slice(0, amountOfEvents);
};

export const NUMBER_OF_MORE_EVENTS_TO_SHOW = 3;

export const getMoreEventsFromThisOrganizerFiltered = (
    eventOwnerId: string,
    events: OrganizerEventResponse[],
): OrganizerEventResponse[] => {
    const openSaleEvents = getOpenSaleEvents(events);
    const filteredEvents = filterEvents(
        NUMBER_OF_MORE_EVENTS_TO_SHOW,
        openSaleEvents,
        eventOwnerId,
    );

    return filteredEvents;
};

export const getOrganizerRelatedEventsFromDestination = async (
    organizerId: string,
    eventId: string,
): Promise<FormattedEventResponse | undefined> => {
    const { events } = await getOrganizerEvents(organizerId);

    const filteredEvents = getMoreEventsFromThisOrganizerFiltered(
        eventId,
        events,
    );
    const relatedEventIds = filteredEvents.map((event) => event.id);

    if (isEmpty(relatedEventIds)) {
        return;
    }

    return sdkRequest(
        _getOrganizerDestinationEventsApiUrl(relatedEventIds.join()),
    )
        .then((response: DestinationResponse) => {
            return {
                events: response.events.map((event: DestinationEvent) =>
                    transformDestinationEvent(event),
                ),
            };
        })
        .catch(translateServerErrors(GENERAL_ERROR_SPEC));
};
